<template>
  <CCard>
    <CCardHeader>
      <CIcon name="cil-notes"/>
      <h5 class="d-inline ml-2">
        {{ this.$route.meta.label }}
        <template>
          : <h3 class="d-inline"><CBadge color="secondary">{{ this.itemName }}</CBadge></h3>
        </template>
      </h5>
      <div class="card-header-actions">
        <CButton color="dark" :to="{ name: 'Devices'}">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <alert-section
        :successAlertMessage="successAlertMessage"
        :dismissSecs="dismissSecs"
        :dismissSuccessAlert="dismissSuccessAlert"
        :errorAlertMessage="errorAlertMessage"
        :showErrorAlert="showErrorAlert"
      />

      <CForm>
        <div class="d-flex flex-column">

          <!-- General -->
          <div class="mb-3">
            <h3 class="font-weight-bold">General</h3>

            <!-- String #ID, Software -->
            <div class="mb-3 d-flex flex-column flex-md-row">
              <CInput
                class="w-100 mr-2"
                label="String #ID"
                placeholder="String #ID"
                :lazy="false"
                :value="form.sid"
                disabled
              />

              <div class="form-group w-100">
                <label class="d-block" style="font-weight: 500">Software:</label>
                <CSwitch
                  :checked="form.software"
                  labelOn="YES"
                  labelOff="NO"
                  color="success"
                  shape="pill"
                  size="lg"
                  :lazy="false"
                  disabled
                />
              </div>
            </div>

            <!-- WS Connection -->
            <fieldset v-if="form.ws_status != null" class="border rounded p-3 bg-light mb-5">
              <legend class="w-auto bg-white rounded py-1 px-2 live-connection-title">
                Live Connection
              </legend>
              <div class="d-flex flex-lg-row flex-column flex-gap-2">
                <CInput
                    v-if="form.ws_status != 1"
                    class="w-100 mr-2 ws-status-text offline"
                    label="Connection Status"
                    placeholder="Connection Status"
                    :lazy="false"
                    value="OFFLINE"
                    disabled
                  />
                  <CInput
                    v-else
                    class="w-100 mr-2 ws-status-text online"
                    label="Connection Status"
                    placeholder="Connection Status"
                    :lazy="false"
                    value="ONLINE"
                    disabled
                  />
                <CInput
                  v-if="form.ws_lastcheck_on"
                  class="w-100 mr-2"
                  label="Last Connection"
                  placeholder="Last Connection"
                  :lazy="false"
                  :value="form.ws_lastcheck_on"
                  disabled
                />
                <CInput
                  v-if="form.ws_lastcheck_off"
                  class="w-100"
                  label="Last Disconnection"
                  placeholder="Last Disconnection"
                  :lazy="false"
                  :value="form.ws_lastcheck_off"
                  disabled
                />
              </div>
            </fieldset>
          </div>

          <!-- Device Information -->
          <div class="mb-3">
            <h3 class="mb-3 font-weight-bold">Device Information</h3>
            <!-- IMEI Number, Model -->
            <div class="mb-3 d-flex flex-column flex-md-row">
              <CInput
                class="w-100 mr-2"
                label="IMEI"
                placeholder="IMEI"
                :lazy="false"
                :value="form.imei"
                disabled
              />

              <CInput
                class="w-100"
                label="Model"
                placeholder="Model"
                :lazy="false"
                :value="form.model" 
                disabled
              />
            </div>

            <!-- Serial Number, SIM Card Number -->
            <div class="mb-3 d-flex flex-column flex-md-row">
              <CInput
                class="w-100 mr-2"
                label="Serial Number"
                placeholder="Serial Number"
                :lazy="false"
                :value="form.serienr"
                disabled
              />

              <CInput
                class="w-100"
                label="SIM Card Number"
                placeholder="SIM Card Number"
                :lazy="false"
                :value="form.simkaartnr"
                disabled
              />
            </div>
            <!-- Versioning -->
            <div class="mb-3 d-flex flex-column flex-md-row">
              <CInput
                v-if="form.version"
                class="w-100 mr-2"
                label="App Version"
                placeholder="App Version"
                :lazy="false"
                :value="form.version"
                disabled
              />
              <CInput
                v-if="form.os"
                class="w-100 mr-2"
                label="OS"
                placeholder="OS"
                :lazy="false"
                :value="form.os"
                disabled
              />
              <CInput
                v-if="form.os_device"
                class="w-100"
                label="Device OS (Only for MB)"
                placeholder="Device OS (Only for MB)"
                :lazy="false"
                :value="form.os_device"
                disabled
              />
            </div>

            <!-- Connectivity, IP -->
            <div v-if="form.connectivity || form.ip" class="mb-3 d-flex flex-column flex-md-row">
              <CInput
                v-if="form.connectivity"
                :class="['w-100', form.ip ? 'mr-2' : '']"
                label="Connectivity"
                placeholder="Connectivity"
                :lazy="false"
                :value="form.connectivity"
                disabled
              />
              <CInput
                v-if="form.ip"
                class="w-100"
                label="IP"
                placeholder="IP"
                :lazy="false"
                :value="form.ip"
                disabled
              />
            </div>
          </div>

          <!-- Orange M2M SIM -->
          <fieldset v-if="form.simkaartnr" class="border rounded p-3 bg-light mb-4">
            <legend class="w-auto bg-white rounded py-1 px-2 orange-sim-title">
              Orange M2M SIM
            </legend>

            <div v-if="orangeSimLoading" class="d-flex align-items-center text-muted">
              <CSpinner size="sm" color="warning" class="mr-2" />
              Reading the current subscription status from Orange...
            </div>

            <CAlert v-if="orangeSimError" color="danger" class="mb-3">
              {{ orangeSimError }}
            </CAlert>

            <CAlert v-if="orangeSimSuccess" color="success" class="mb-3">
              {{ orangeSimSuccess }}
            </CAlert>

            <div v-if="orangeSim && !orangeSimLoading">
              <div class="d-flex flex-column flex-lg-row mb-3">
                <div class="w-100 mr-lg-3 mb-2 mb-lg-0">
                  <label class="d-block mb-1">Current subscription status</label>
                  <CBadge :color="orangeSimStatusColor" class="orange-sim-status px-3 py-2">
                    {{ orangeSim.status || 'UNKNOWN' }}
                  </CBadge>
                </div>
                <CInput
                  class="w-100"
                  label="Last status change"
                  :value="orangeSim.last_subscription_date_change || 'Unknown'"
                  disabled
                />
              </div>

              <div v-if="canChangeOrangeSim" class="d-flex flex-column flex-lg-row align-items-lg-end">
                <CSelect
                  class="w-100 mr-lg-3 mb-2 mb-lg-0"
                  label="Change subscription status"
                  placeholder="Select a status..."
                  :options="orangeSimStateOptions"
                  :value.sync="orangeSimTargetState"
                  :disabled="orangeSimChanging"
                />
                <CButton
                  color="warning"
                  class="text-nowrap"
                  :disabled="!canSubmitOrangeSimChange"
                  @click="changeOrangeSimStatus"
                >
                  <CSpinner v-if="orangeSimChanging" size="sm" class="mr-1" />
                  Change status
                </CButton>
              </div>

              <CAlert v-if="orangeSimPendingRequest" color="info" class="mt-3 mb-0">
                Orange accepted the request to change from
                <strong>{{ orangeSimPendingRequest.previous_state }}</strong> to
                <strong>{{ orangeSimPendingRequest.requested_state }}</strong>.
                Request ID: <strong>{{ orangeSimPendingRequest.subscription_request_id }}</strong>.
                The change is asynchronous; refresh the status to see when it is complete.
              </CAlert>
            </div>

            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              class="mt-3"
              :disabled="orangeSimLoading || orangeSimChanging"
              @click="loadOrangeSimStatus"
            >
              <CIcon name="cil-reload" class="mr-1" />
              Refresh SIM status
            </CButton>
          </fieldset>

          <!-- Notes -->
          <div class="mb-3 d-flex flex-column flex-md-row">
            <CTextarea
              class="w-100"
              rows="2"
              label="Notes"
              placeholder="Notes"
              :lazy="false"
              :value.sync="$v.form.notities.$model"
            />
          </div>

          <!-- Linked Restaurant -->
          <div class="mb-3 d-flex flex-column flex-md-row">
            <div class="form-group w-100 mr-2">
              <label class="d-block">Restaurant:</label>
              <v-select
                :reduce="c => c.value"
                placeholder="Please select.."
                :searchable="true"
                v-model="$v.form.restaurant.$model"
                :options="restaurants"
              />
            </div>

            <div v-if="form.restaurant" role="group" class="w-100 form-group">
              <label class="d-block">&nbsp;</label>
              <CButton
                color="danger"
                size="sm"
                shape="pill"
                @click="() => form.restaurant = null"
              >
                <CIcon name="cil-x" class="mb-1"/>&nbsp;Unlink
              </CButton>
            </div>
          </div>
        </div>
      </CForm>
    </CCardBody>

    <CCardFooter>
      <CButton
        color="primary"
        :disabled="submitted"
        @click="submit()"
      >
        Submit
      </CButton>
    </CCardFooter>

    <CElementCover :opacity="0.4" v-show="submitted"/>

  </CCard>
</template>

<script>
import { validationMixin } from "vuelidate"

export default {
  name: 'DeviceItem',

  data() {
    return {
      form: {},
      submitted: false,

      itemName: '',
      restaurants: [],

      // Alert işlemleri için
      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      orangeSim: null,
      orangeSimLoading: false,
      orangeSimChanging: false,
      orangeSimTargetState: null,
      orangeSimError: null,
      orangeSimSuccess: null,
      orangeSimPendingRequest: null,
    }
  },

  computed: {
    isValid () { return !this.$v.form.$invalid },
    isDirty () { return this.$v.form.$anyDirty },
    itemId() { return this.$route.params.id },
    canChangeOrangeSim() {
      return this.isSuperAdmin
        || (Array.isArray(this.authUserRoles)
          && this.authUserRoles.some(role => ['SuperAdmin', 'Admin'].includes(role)))
    },
    orangeSimStateOptions() {
      if (!this.orangeSim || !Array.isArray(this.orangeSim.allowed_state_changes)) {
        return []
      }

      return this.orangeSim.allowed_state_changes.map(state => ({
        value: state,
        label: state === 'ACTIVE' ? 'ACTIVE (enable)' : 'PAUSE (disable)',
      }))
    },
    orangeSimStatusColor() {
      if (!this.orangeSim) return 'secondary'

      if (this.orangeSim.status === 'ACTIVE') return 'success'
      if (this.orangeSim.status === 'PAUSE') return 'warning'

      return 'secondary'
    },
    canSubmitOrangeSimChange() {
      return this.canChangeOrangeSim
        && !this.orangeSimChanging
        && !this.orangeSimPendingRequest
        && this.orangeSimTargetState
        && this.orangeSim
        && this.orangeSimTargetState !== this.orangeSim.status
    }
  },

  mixins: [validationMixin],

  validations: {
    form: {
      notities: { },
      restaurant: { }
    }
  },

  created () {
    if(this.itemId) {
      this.form = this.getForm(this.itemId)
    }
    else
      this.form = this.getForm()
  },

  mounted() {
    this.getRestaurantNames();
  },

  methods: {
    checkIfValid (fieldName) {
      const field = this.$v.form[fieldName]
      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === '')
    },

    async submit () {
      if (!this.isValid) {
        this.validate();
        return;
      }

      await this.update()
    },

    async update() {
      this.submitted = true;
      await this.$axios.put(this.$backend.DEVICE.UPDATE.replace("{id}", this.itemId), {'sid': this.form.sid, 'restaurant': this.form.restaurant, 'software': this.form.software, 'notities': this.form.notities})
        .then((response)=>{
          this.itemName = response.data.sid
          this.successAlertMessage = {itemName: this.itemName, message: 'device updated.'}
          this.dismissSuccessAlert = this.dismissSecs
          this.showErrorAlert = false
        })
        .catch((error)=>{
          this.errorAlertMessage = error.response.data
          this.showErrorAlert = true
        })
        .finally(() => {
          this.submitted = false
        });
    },

    async loadOrangeSimStatus() {
      if (!this.itemId || !this.form.simkaartnr) {
        this.orangeSim = null
        return
      }

      this.orangeSimLoading = true
      this.orangeSimError = null
      this.orangeSimSuccess = null

      try {
        const { data } = await this.$axios.get(
          this.$backend.DEVICE.SIM_SUBSCRIPTION.replace("{id}", this.itemId)
        )

        this.orangeSim = data
        this.orangeSimTargetState = Array.isArray(data.allowed_state_changes)
          && data.allowed_state_changes.includes(data.status)
          ? data.status
          : null
        this.orangeSimPendingRequest = null
      } catch (error) {
        this.orangeSim = null
        this.orangeSimError = error.response?.data?.message
          || 'Could not read the Orange M2M subscription status.'
      } finally {
        this.orangeSimLoading = false
      }
    },

    async changeOrangeSimStatus() {
      if (!this.canSubmitOrangeSimChange) return

      const requestedState = this.orangeSimTargetState
      const currentState = this.orangeSim.status
      const confirm = await this.mSwal.fire({
        title: 'Change Orange SIM status?',
        text: `SIM ${this.form.simkaartnr} will change from `
          + `${currentState} to ${requestedState}.`,
        icon: requestedState === 'PAUSE' ? 'warning' : 'question',
        confirmButtonText: `Yes, change to ${requestedState}`,
      })

      if (!confirm.isConfirmed) return

      this.orangeSimChanging = true
      this.orangeSimError = null
      this.orangeSimSuccess = null

      try {
        const { data } = await this.$axios.patch(
          this.$backend.DEVICE.SIM_SUBSCRIPTION.replace("{id}", this.itemId),
          { state: requestedState }
        )

        if (data.changed) {
          this.orangeSimPendingRequest = data
          this.orangeSimSuccess = data.message
        } else {
          this.orangeSim = data.subscription
          this.orangeSimTargetState = data.subscription.status
          this.orangeSimPendingRequest = null
          this.orangeSimSuccess = data.message
        }
      } catch (error) {
        this.orangeSimError = error.response?.data?.message
          || 'Could not change the Orange M2M subscription status.'
      } finally {
        this.orangeSimChanging = false
      }
    },

    validate () {
      this.$v.$touch()
    },

    reset () {
      this.form = this.getForm()
      this.submitted = false
      this.showErrorAlert = false
      this.$v.$reset()
    },

    getForm (id = null) {
      var form = {
        sid: null,
        serienr: null,
        simkaartnr: null,
        notities: null,
        software: this.$store.state.deviceSoftware,
        version: null,
        os: null,
        os_device: null,
        imei: null,
        model: null,
        ip: null,
        restaurant: null,
        connectivity: null,
        ws_lastcheck_off: null,
        ws_lastcheck_on: null,
        ws_status: null
      }

      if(id) {
        this.$axios
          .get(
            this.$backend.DEVICE.SHOW.replace("{id}", id)
          )
          .then((res) => {
            form.sid =  res.data.sid;
            form.serienr =  res.data.serienr;
            form.simkaartnr =  res.data.simkaartnr;
            form.sid =  res.data.sid;
            form.notities =  res.data.notities;
            form.software =  res.data.software;
            form.version =  res.data.version;
            form.os =  res.data.os;
            form.os_device =  res.data.os_device;
            form.imei =  res.data.imei;
            form.model =  res.data.model;
            form.ip = res.data.ip;
            form.connectivity = res.data.connectivity;
            form.ws_lastcheck_off = res.data.ws_lastcheck_off;
            form.ws_lastcheck_on = res.data.ws_lastcheck_on;
            form.ws_status = res.data.ws_status;

            form.restaurant =  res.data.restaurants.length > 0 ? res.data.restaurants[0].id : null;

            this.itemName = res.data.sid;
            this.loadOrangeSimStatus();
          })
          .catch(() => {
            this.$router.push({ name: 'Devices' })
          })
      }
      return form;
    },

    getRestaurantNames() {
      this.$axios
        .get(this.$backend.RESTAURANT.GET_ALL_NAME)
        .then((response) => {
          this.restaurants = response.data.sort((a,b) => b.value - a.value);
        })
        .catch(() => { this.restaurants = [] })
    },
  }
}
</script>

<style>
input {
  background-color: white !important;
}

.live-connection-title {
  font-weight: bold;
  font-size: 1.25rem;
}

.orange-sim-title {
  font-weight: bold;
  font-size: 1.25rem;
}

.orange-sim-status {
  font-size: 0.95rem;
  letter-spacing: 0.03rem;
}

.ws-status-text input {
  font-weight: bold;
}

.online input {
  color: green;
}

.offline input {
  color: red;
}
</style>
